import React, { ForwardedRef, forwardRef, useContext, useEffect, useMemo, useState } from "react";
import { IDowntimeItem } from "./DowntimeItemInterfaces";
import StraightIcon from "@mui/icons-material/Straight";
import ErrorIcon from "@mui/icons-material/Error";
import { useStyles } from "./DowntimeItem.styles";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";
import moment from "moment";
import "moment-duration-format";
import { useTranslation } from "react-i18next";
import {
    END_OF_SHIFT_FAILURE_CODE_NAME,
    FAILURE_TYPES_CODE_EXCEPTIONS,
    MEASURE_METRICS,
    IconsTrafficLightStatusCodes,
    UPDATEMINUTESRATEFORHMIDATA,
} from "constants/constants";
import { CircularProgress, Tooltip, Typography } from "@mui/material";
import { calculateShiftDate, getCurrentShift, percentageChecker } from "utilities/utilities";
import { IShift } from "../../LiveDowntimeReportAPIInterfaces";
import { HMIContext } from "contexts/HMIContext/HMIContext";
import { ShiftOptionsEnum } from "components/ShiftDropdown/ShiftDropdown.view";
import { MachineCenterTrafficLights } from "components/MachineCenterTrafficLights/MachineCenterTrafficLights.view";

const DowntimeItem = ({ downtimeData, isSelected, onClick }: IDowntimeItem, ref: ForwardedRef<HTMLDivElement>) => {
    const classes = useStyles();
    const { showDetails } = useContext(MillDashboardContext);
    const [nowDate, setNowDate] = useState(moment().tz(downtimeData.millTimezone));
    const { t } = useTranslation();
    const [uptimePercentage, setUptimePercentage] = useState<null | number>(null);
    const [downtimePercentage, setDowntimePercentage] = useState<null | number>(null);
    const currentTime = moment().tz(downtimeData.millTimezone).format("HH:mm:ss");
    const [waitingForUpdateLastDowntimeSeconds, setWaitingForUpdateLastDowntimeSeconds] = useState<boolean>(false);
    const [machineCenterCurrentState, setMachineCenterCurrentState] = useState<IconsTrafficLightStatusCodes>(
        downtimeData.status,
    );
    const [noPercentageData, setNoPercentageData] = useState<boolean>(false);
    const [currentShift, setCurrentShift] = useState<IShift | undefined>(undefined);
    const { shift } = useContext(HMIContext);

    const selectedShiftDataInSeconds =
        shift === ShiftOptionsEnum.current
            ? downtimeData.lastTwoShiftsEvents?.[downtimeData.id].currentShiftTimes
            : downtimeData.lastTwoShiftsEvents?.[downtimeData.id].previousShiftTimes;

    useEffect(() => {
        const oneSecondinterval = setInterval(() => {
            setNowDate(moment().tz(downtimeData.millTimezone));
        }, 1000);
        return () => clearInterval(oneSecondinterval);
    }, [downtimeData.millTimezone]);

    const nowMinusDowntimeSeconds = useMemo(() => {
        const now = moment();
        return now.subtract(downtimeData.lastTwoShiftsEvents?.[downtimeData.id].currentShiftTimes.downtime, "seconds");
    }, [downtimeData]);

    const currentDowntime = useMemo(() => {
        if (
            (machineCenterCurrentState === IconsTrafficLightStatusCodes.DOWN ||
                machineCenterCurrentState === IconsTrafficLightStatusCodes.BELOW_TRIGGER) &&
            downtimeData.current
        ) {
            const nowDateParsed = moment(nowDate, "DD/MM/YYYY HH:mm:ss");
            if (currentShift) {
                const shiftHourValues = currentShift.shift_historic[0]?.start_time.split(":");

                const shiftWithDate = calculateShiftDate(
                    Number(shiftHourValues[0]),
                    Number(shiftHourValues[1]),
                    Number(shiftHourValues[2]),
                    true,
                    downtimeData.millTimezone,
                );

                const maxStartTime = moment.max(moment.utc(downtimeData.current), shiftWithDate);
                const currentTime = moment.duration(nowDateParsed.diff(maxStartTime));

                return currentTime.format("hh:mm:ss", { trim: false });
            }
        }
        return "00:00:00";
    }, [downtimeData, nowDate, currentShift, machineCenterCurrentState]);

    const totalDowntime = useMemo(() => {
        // if there is a current shift
        if (currentShift) {
            if (shift === ShiftOptionsEnum.current) {
                // the machine is up, there is data for the last two shifts and the shift selected is current
                if (machineCenterCurrentState === IconsTrafficLightStatusCodes.UP && selectedShiftDataInSeconds) {
                    const currentAccruedDowntime =
                        downtimeData.lastTwoShiftsEvents?.[downtimeData.id].currentShiftTimes.downtime;

                    const currentDuration = moment.duration(currentAccruedDowntime, "seconds");
                    return currentDuration.format("hh:mm:ss", { trim: false });
                }

                //if the machine is down, there is a current event, there is data in the last two shifts endpoint and the shift selected is current
                if (
                    (machineCenterCurrentState === IconsTrafficLightStatusCodes.DOWN ||
                        machineCenterCurrentState === IconsTrafficLightStatusCodes.BELOW_TRIGGER) &&
                    downtimeData.current
                ) {
                    if (
                        (downtimeData.lastTwoShiftsEvents?.[downtimeData.id].currentShiftTimes.downtime as number) < 1
                    ) {
                        return currentDowntime;
                    } else {
                        const nowDateParsed = moment(nowDate, "DD/MM/YYYY HH:mm:ss");
                        const currentAccruedTime = moment.duration(nowDateParsed.diff(nowMinusDowntimeSeconds));

                        const formattedCurrentAccruedTime = currentAccruedTime.format("hh:mm:ss", { trim: false });

                        return formattedCurrentAccruedTime < currentDowntime
                            ? currentDowntime
                            : formattedCurrentAccruedTime;
                    }
                }
            }

            // if the shift selected is previous
            if (shift !== ShiftOptionsEnum.current) {
                // if there is previousShiftData
                if (selectedShiftDataInSeconds?.downtime && selectedShiftDataInSeconds) {
                    const currentAccruedDowntime = selectedShiftDataInSeconds.downtime;

                    const currentDuration = moment.duration(currentAccruedDowntime, "seconds");
                    return currentDuration.format("hh:mm:ss", { trim: false });
                }

                // if there is no previousShiftData
                if (!selectedShiftDataInSeconds?.downtime) {
                    const currentDuration = moment.duration("0", "seconds");
                    return currentDuration.format("hh:mm:ss", { trim: false });
                }
            }

            const currentAccruedTime = moment.duration(selectedShiftDataInSeconds?.downtime, "seconds");
            return currentAccruedTime.format("hh:mm:ss", { trim: false });
        }
    }, [
        downtimeData,
        shift,
        currentShift,
        selectedShiftDataInSeconds,
        nowDate,
        nowMinusDowntimeSeconds,
        currentDowntime,
        machineCenterCurrentState,
    ]);

    useEffect(() => {
        const getPercentagesData = async () => {
            if (selectedShiftDataInSeconds) {
                const downtimePercentageCalculated = Math.ceil(
                    (100 * selectedShiftDataInSeconds.downtime) / selectedShiftDataInSeconds.shift,
                );

                if (waitingForUpdateLastDowntimeSeconds) {
                    setWaitingForUpdateLastDowntimeSeconds(false);
                }

                const uptimePercentageCalculated = Math.floor(
                    (100 * (selectedShiftDataInSeconds.shift - selectedShiftDataInSeconds.downtime)) /
                        selectedShiftDataInSeconds.shift,
                );
                if (selectedShiftDataInSeconds.downtime === 0) {
                    setDowntimePercentage(0);
                    setUptimePercentage(100);
                } else {
                    setDowntimePercentage(percentageChecker(downtimePercentageCalculated));
                    setUptimePercentage(percentageChecker(uptimePercentageCalculated));
                }
                setNoPercentageData(false);
            } else {
                setNoPercentageData(true);
            }
        };

        getPercentagesData();

        const interval = setInterval(async () => {
            await getPercentagesData();
        }, UPDATEMINUTESRATEFORHMIDATA);

        return () => clearInterval(interval);
    }, [
        downtimeData.machineCenterId,
        downtimeData.lastTwoShiftsEvents,
        waitingForUpdateLastDowntimeSeconds,
        selectedShiftDataInSeconds,
    ]);

    // shift ended calculation
    const shiftEnded = useMemo(() => {
        const now = moment(currentTime, "HH:mm:ss").tz(downtimeData.millTimezone);

        //sort shifts and filter them by effective date
        const sortedShifts = downtimeData.productionLine.production_line_shifts.slice().map((shift) => ({
            ...shift,
            shift: {
                ...shift.shift,
                shift_historic: shift.shift.shift_historic
                    .slice()
                    .sort((a, b) => new Date(b.effective_at).getTime() - new Date(a.effective_at).getTime())
                    .filter((shift) =>
                        moment(shift.effective_at)
                            .tz(downtimeData.millTimezone)
                            .isSameOrBefore(moment().tz(downtimeData.millTimezone)),
                    ),
            },
        }));

        // find the previous shift if any
        const previousShifts = sortedShifts
            .filter(
                (shift) =>
                    now >= moment(shift.shift.shift_historic[0]?.end_time, "HH:mm:ss").tz(downtimeData.millTimezone),
            )
            .sort((a, b) => {
                const timeA = moment.duration(a.shift.shift_historic[0].start_time).asSeconds();
                const timeB = moment.duration(b.shift.shift_historic[0].start_time).asSeconds();

                return timeB - timeA;
            });

        //shift end time formatted
        const previousShiftEndTimeFormatted = moment
            .tz(
                `${moment().format("YYYY-MM-DD")} ${previousShifts[0]?.shift.shift_historic[0].end_time}`,
                downtimeData.millTimezone,
            )
            .format();

        if (previousShifts.length > 0 && machineCenterCurrentState === IconsTrafficLightStatusCodes.UP) {
            //add to the previous shift's end_time the maximum shift overtime
            const shiftPlusOvertime = moment(previousShifts[0].shift.shift_historic[0]?.end_time, "HH:mm:ss").add(
                downtimeData.millOvertime,
                "minutes",
            );
            const overtimeFormatted = shiftPlusOvertime.format("HH:mm:ss");

            if (now.isBefore(moment(overtimeFormatted, "HH:mm:ss"))) {
                if (
                    downtimeData.lastTwoShiftsEvents &&
                    downtimeData.lastTwoShiftsEvents[downtimeData.machineCenterId] &&
                    downtimeData.lastTwoShiftsEvents[downtimeData.machineCenterId].currentShiftEventWithHours
                ) {
                    const lastShiftEvent = downtimeData.lastTwoShiftsEvents[
                        downtimeData.machineCenterId
                    ].currentShiftEventWithHours.filter((event) => {
                        return (
                            moment(event.start_date)
                                .tz(downtimeData.millTimezone)
                                .isSameOrBefore(previousShiftEndTimeFormatted) &&
                            moment(event.end_date)
                                .tz(downtimeData.millTimezone)
                                .isSameOrAfter(previousShiftEndTimeFormatted)
                        );
                    });

                    const eventsDuringOvertime = downtimeData.lastTwoShiftsEvents[
                        downtimeData.machineCenterId
                    ].currentShiftEventWithHours.filter((event) => {
                        return moment(event.start_date)
                            .tz(downtimeData.millTimezone)
                            .isSameOrAfter(previousShiftEndTimeFormatted);
                    });

                    let endOfShiftPreviousEvent = false;

                    if (downtimeData.failure_code === END_OF_SHIFT_FAILURE_CODE_NAME) {
                        const eventEndTime = moment.utc(downtimeData.current);
                        const shiftPlusOvertimeFormatted = moment(shiftPlusOvertime);

                        const eventEndTimeDateFormatted = eventEndTime
                            .tz(downtimeData.millTimezone)
                            .format("YYYY-MM-DDTHH:mm:ss.SSS");
                        const shiftPlusOvertimeDateFormatted =
                            shiftPlusOvertimeFormatted.format("YYYY-MM-DDTHH:mm:ss.SSS");

                        endOfShiftPreviousEvent =
                            eventEndTimeDateFormatted >= previousShiftEndTimeFormatted &&
                            eventEndTimeDateFormatted <= shiftPlusOvertimeDateFormatted;
                    }

                    //we determine if the shift has ended based on the last event in the shift being a downtime or at least one event during the overtime period is a downtime or, as we're not getting end of shift events from the backend, there has been an end of shift event during the downtime

                    const endOfShift =
                        lastShiftEvent[0]?.measure_metric_id === MEASURE_METRICS.DOWNTIME ||
                        eventsDuringOvertime.some((event) => event.measure_metric_id === MEASURE_METRICS.DOWNTIME) ||
                        endOfShiftPreviousEvent;

                    if (endOfShift) {
                        return endOfShift;
                    }
                }

                setCurrentShift(previousShifts[0].shift);
                return !previousShifts[0];
            }
        }
        //find the current shift if any
        const currentShift = getCurrentShift(sortedShifts, downtimeData.millTimezone, currentTime);
        setCurrentShift(currentShift?.shift);

        //return the current shift if any: if there is one, the shiftEnded value would be false, otherwise, it would be true
        return !currentShift;
    }, [downtimeData, currentTime, machineCenterCurrentState]);

    useEffect(() => {
        const currentDownTimeSplitted = currentDowntime.split(":");
        const currentDownTimeInSeconds =
            +currentDownTimeSplitted[0] * 3600 + +currentDownTimeSplitted[1] * 60 + +currentDownTimeSplitted[2];

        if (downtimeData.status === IconsTrafficLightStatusCodes.DOWN) {
            //the machine is down due to an end_of_shift
            if (downtimeData.failure_code === END_OF_SHIFT_FAILURE_CODE_NAME) {
                setMachineCenterCurrentState(IconsTrafficLightStatusCodes.END_OF_SHIFT);
                //there is no active shift
            } else if (shiftEnded) {
                setMachineCenterCurrentState(IconsTrafficLightStatusCodes.END_OF_SHIFT);
                //there is a below trigger downtime and an active shift
            } else if (downtimeData.failureTypeCode === FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER) {
                if (currentDownTimeInSeconds < downtimeData.downtime_trigger_threshold) {
                    setMachineCenterCurrentState(IconsTrafficLightStatusCodes.BELOW_TRIGGER);
                } else {
                    setMachineCenterCurrentState(IconsTrafficLightStatusCodes.DOWN);
                }
                //there is an active shift
            } else {
                setMachineCenterCurrentState(IconsTrafficLightStatusCodes.DOWN);
            }
        } else if (downtimeData.status === IconsTrafficLightStatusCodes.UP) {
            //there is no active shift
            if (shiftEnded) {
                setMachineCenterCurrentState(IconsTrafficLightStatusCodes.END_OF_SHIFT);

                //there is an active shift
            } else {
                setMachineCenterCurrentState(IconsTrafficLightStatusCodes.UP);
            }
        }
    }, [
        downtimeData.failure_code,
        downtimeData.status,
        shiftEnded,
        currentDowntime,
        downtimeData.downtime_trigger_threshold,
        downtimeData.failureTypeCode,
    ]);

    const valuesStyles = useMemo(() => {
        if (machineCenterCurrentState === IconsTrafficLightStatusCodes.UP) {
            return classes.value;
        }
        if (machineCenterCurrentState === IconsTrafficLightStatusCodes.OFFLINE) {
            return classes.value;
        }
        if (machineCenterCurrentState === IconsTrafficLightStatusCodes.DOWN) {
            return classes.valueWithError;
        }
        if (machineCenterCurrentState === IconsTrafficLightStatusCodes.END_OF_SHIFT) {
            return classes.shiftEnded;
        }
        return classes.value;
    }, [classes.value, classes.valueWithError, classes.shiftEnded, machineCenterCurrentState]);

    return (
        <Tooltip
            placement="top"
            PopperProps={{
                className: classes.tooltip,
            }}
            title={
                <Typography className={classes.tooltipTexts}>
                    {`${downtimeData.name}`} {downtimeData.shortName && `| ${downtimeData.shortName}`}
                </Typography>
            }
        >
            <div
                className={`${classes.downtimeWrapper} ${isSelected && classes.downtimeWrapperSelected}`}
                onClick={onClick}
                ref={ref}
            >
                <MachineCenterTrafficLights
                    status={machineCenterCurrentState}
                    isBottleneck={downtimeData.isBottleneck}
                    noPercentageData={noPercentageData}
                />

                <div className={classes.name}>{downtimeData.shortName || downtimeData.name}</div>
                {(showDetails ||
                    (!showDetails &&
                        machineCenterCurrentState !== IconsTrafficLightStatusCodes.DOWN &&
                        machineCenterCurrentState !== IconsTrafficLightStatusCodes.END_OF_SHIFT)) &&
                    (noPercentageData ? (
                        <div className={classes.noDataWrapper}>
                            <ErrorIcon className={classes.noDataIcon} />
                            <Typography variant="error">{t("data.no_data_short")}</Typography>
                        </div>
                    ) : uptimePercentage !== null && downtimePercentage !== null ? (
                        <div className={classes.downtime}>
                            <div className={classes.downtimeUp}>
                                {`${uptimePercentage}%`}
                                <StraightIcon />
                            </div>
                            <div className={classes.downtimeDown}>
                                {`${downtimePercentage}%`}
                                <StraightIcon />
                            </div>
                        </div>
                    ) : (
                        <CircularProgress size={20} color="info" />
                    ))}

                {showDetails && (
                    <div className={classes.dataWrapper}>
                        <div className={classes.label}>{t("locations.mill_dashboards.downtime_item.total_dt")}</div>
                        {machineCenterCurrentState !== IconsTrafficLightStatusCodes.END_OF_SHIFT ? (
                            <div className={valuesStyles}>{totalDowntime}</div>
                        ) : noPercentageData ? (
                            <div className={classes.noDataWrapper}>
                                <ErrorIcon className={classes.noDataIcon} />
                                <div>{t("data.no_data_short")}</div>
                            </div>
                        ) : (
                            <div className={valuesStyles}>
                                {t("locations.mill_dashboards.downtime_item.shift_ended").toUpperCase()}
                            </div>
                        )}
                    </div>
                )}

                <div className={classes.dataWrapper}>
                    {showDetails && (
                        <div className={classes.label}>{t("locations.mill_dashboards.downtime_item.current")}</div>
                    )}
                    {machineCenterCurrentState === IconsTrafficLightStatusCodes.UP && showDetails && (
                        <div className={valuesStyles}>00:00:00</div>
                    )}
                    {(machineCenterCurrentState === IconsTrafficLightStatusCodes.DOWN ||
                        machineCenterCurrentState === IconsTrafficLightStatusCodes.BELOW_TRIGGER) && (
                        <div className={valuesStyles}>{currentDowntime}</div>
                    )}
                    {machineCenterCurrentState === IconsTrafficLightStatusCodes.END_OF_SHIFT &&
                        (noPercentageData ? (
                            <div className={classes.noDataWrapper}>
                                <ErrorIcon className={classes.noDataIcon} />
                                <Typography variant="error">{t("data.no_data_short")}</Typography>
                            </div>
                        ) : (
                            <div className={valuesStyles}>
                                {t("locations.mill_dashboards.downtime_item.shift_ended").toUpperCase()}
                            </div>
                        ))}
                </div>
            </div>
        </Tooltip>
    );
};

export default forwardRef(DowntimeItem);
